import * as _ from 'lodash';
import * as Pluralize from 'pluralize';

// import dictionaries
import {
  FractionSymbols,
  NumberWords,
  MeasurementTypes,
  MeasurementAlternatives,
  ItemByDept
} from './dictionaries';

export function ParseDescription(description : string) {
  let parsedInfo = {
    qty           : null,
    unit          : null,
    unitType      : null,
    item          : null,
    lowercaseItem : null,
    dept          : null
  };

  // clean description
  description = _cleanDescription(description);

  if (description == null || description === '') return parsedInfo;

  // split description
  let descriptionArr = _splitDescription(description);

  // attempt to parse quantity
  _parseQuantity(descriptionArr, (qty, remainingArr) => {
    parsedInfo.qty = qty || 1;

    descriptionArr = remainingArr;
  });

  // attempt to parse unit
  _parseUnit(descriptionArr, (unitObj, remainingArr) => {
    parsedInfo.unit = unitObj.unit || null;
    parsedInfo.unitType = unitObj.unitType || 'quantity';

    descriptionArr = remainingArr;
  });

  // parse item
  _parseItem(descriptionArr, itemObj => {
    parsedInfo.item          = itemObj.item;
    parsedInfo.lowercaseItem = itemObj.lowercaseItem;
  });

  // parse dept
  ParseDept(descriptionArr, dept => {
    parsedInfo.dept = dept;
  });

  return parsedInfo;
}

function _cleanDescription(description : string) : string {
  // remove preceding, trailing, or duplicate spaces
  description = description.replace(/^\s|\s$|\s\s/g, '');

  // ensure description has proper spaces between all pieces
  // if an alpha character (case insensitive a-z) follows a non-alpha character (besides a space), insert a space
  description = description.replace(/[^a-z\s-](?=[a-z¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/gi, '$& ');

  return description;
}

function _splitDescription(description : string) : string[] {
  // split pieces using spaces
  return description.split(/\s/);
}

function _parseQuantity(descriptionArr, callback) : void {
  let potentialQty1 = _testQuantity(descriptionArr[0]),
    potentialQty2 = descriptionArr[1] ? _testQuantity(descriptionArr[1]) : null;

  if (potentialQty1 !== null) {
    descriptionArr.splice(0, 1);

    if (potentialQty2 !== null) {
      potentialQty1 += potentialQty2;

      descriptionArr.splice(0, 1);
    }
  }

  callback(potentialQty1, descriptionArr);
}

function _testQuantity(piece : string) {
  // evaluate only lowercase string
  piece = piece.toLowerCase();

  // test for fractions
  if (/\//.test(piece))
    return _fractionToDecimal(piece);
  // test for numbers
  else if (!isNaN(parseFloat(piece)))
    return parseFloat(piece);
  // test for fractions as HTML symbols
  else if (FractionSymbols[piece] != null)
    return FractionSymbols[piece];
  // test for numbers written out as words
  else if (NumberWords[piece] != null)
    return NumberWords[piece];
  // not a number
  else
    return null;
}

function _fractionToDecimal(piece : string) : number {
  const fractionArr = piece.split(/\//);

  return parseInt(fractionArr[0], 10) / parseInt(fractionArr[1], 10);
}

function _parseUnit(descriptionArr, callback) : void {
  let potentialUnit : object = null;

  for (let i = 0; i < descriptionArr.length; i++) {
    potentialUnit = _testUnit(descriptionArr[i]);

    if (potentialUnit !== null) {
      descriptionArr.splice(i, 1);

      break;
    } else if (descriptionArr[i + 1]) {
      potentialUnit = _testUnit(descriptionArr.slice(i, i + 2).join(' '));

      if (potentialUnit !== null) {
        descriptionArr.splice(i, 2);

        break;
      }
    }
  }

  callback(potentialUnit || {}, descriptionArr);
}

function _testUnit(piece : string) : object {
  // evaluate only lowercase string
  piece = piece.toLowerCase();

  // attempt to locate measurement
  let unit = MeasurementTypes[piece] ? piece : MeasurementAlternatives[piece];

  // if nothing is found try a singularized version
  if (unit == null) {
    piece = Pluralize.singular(piece);

    unit = MeasurementTypes[piece] ? piece : MeasurementAlternatives[piece];
  }

  return unit == null ? null : {
    unit     : unit,
    unitType : MeasurementTypes[unit]
  };
}

function _parseItem(descriptionArr, callback) : void {
  // if first word is "of", remove it
  if (descriptionArr[0].toLowerCase() === 'of')
    descriptionArr.shift();

  const item = descriptionArr.join(' ');

  callback({
    item,
    lowercaseItem : item.toLowerCase()
  });
}

export function ParseDept(descriptionArr : string[], callback) : void {
  // 1. test all words
  // 2. remove 1 from the end & test all remaining words
  // 3. repeat step #2 until only 2 words are left
  // 4. remove 1 from the beginning & test all remaining words
  // 5. repeat steps #2 - #4 until only 2 words are left
  // 6. try each word, one at a time, from beginning to end

  let dept    : string   = null,
    iteration : number   = 0,
    testArr   : string[] = descriptionArr.slice();

  while (testArr.length > 1) {
    dept = _testDept(testArr.join(' '));

    // if we caught something, end the loop
    if (dept !== null)
      break;

    // remove 1 member from the end
    testArr.pop();

    // test if current array has 1 or fewer members
    if (testArr.length <= 1) {
      // remove 1 from the beginning
      testArr = descriptionArr.slice(++iteration);
    }
  }

  // assuming we haven't yet caught something…
  if (dept === null) {
    // test each word, 1 at a time
    for (const word of descriptionArr) {
      dept = _testDept(word);

      // if we caught something, end the loop
      if (dept !== null)
        break;
    }
  }

  // when done looping, fire callback
  callback(dept);
}

function _testDept(piece : string) : string {
  // clean piece:
  // remove accent marks, make lowercase, and remove any other non-word symbols
  piece = _.deburr(piece.toLowerCase()).replace(/[^\w\-\s]/g, '');

  // attempt to locate dept
  // if nothing is found try a singularized version
  return ItemByDept[piece] || ItemByDept[Pluralize.singular(piece)] || null;
}


// WEBPACK FOOTER //
// /Users/briznad/Documents/WebDev/personal/cartmigo/cartmigo-beta/src/app/item-parser.ts